// This file is auto-generated by @hey-api/openapi-ts

import { queryOptions, type UseMutationOptions } from '@tanstack/react-query'

import type {
  GetMeData,
  GetLoginIdentityProvidersData,
  LoginIdentityProviderData,
  LogoutData,
  LogoutError,
  GetSiteData,
  ListMapsData,
  GetMapData,
  ListDatasetsData
} from '../types.gen'
import type { AxiosError } from 'axios'

import { client as _heyApiClient } from '../client.gen'
import { type Options, getMe, getLoginIdentityProviders, loginIdentityProvider, logout, getSite, listMaps, getMap, listDatasets } from '../sdk.gen'

export type QueryKey<TOptions extends Options> = [
  Pick<TOptions, 'baseURL' | 'body' | 'headers' | 'path' | 'query'> & {
    _id: string
    _infinite?: boolean
    tags?: ReadonlyArray<string>
  }
]

const createQueryKey = <TOptions extends Options>(id: string, options?: TOptions, infinite?: boolean, tags?: ReadonlyArray<string>): [QueryKey<TOptions>[0]] => {
  const params: QueryKey<TOptions>[0] = { _id: id, baseURL: options?.baseURL || (options?.client ?? _heyApiClient).getConfig().baseURL } as QueryKey<TOptions>[0]
  if (infinite) {
    params._infinite = infinite
  }
  if (tags) {
    params.tags = tags
  }
  if (options?.body) {
    params.body = options.body
  }
  if (options?.headers) {
    params.headers = options.headers
  }
  if (options?.path) {
    params.path = options.path
  }
  if (options?.query) {
    params.query = options.query
  }
  return [params]
}

export const getMeQueryKey = (options?: Options<GetMeData>) => createQueryKey('getMe', options)

/**
 * Get the details of the logged in user
 * Returns the details of the logged in user.
 *
 */
export const getMeOptions = (options?: Options<GetMeData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getMe({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true
      })
      return data
    },
    queryKey: getMeQueryKey(options)
  })
}

export const getLoginIdentityProvidersQueryKey = (options?: Options<GetLoginIdentityProvidersData>) => createQueryKey('getLoginIdentityProviders', options)

/**
 * List available login methods
 * Returns the list of available login methods.
 *
 */
export const getLoginIdentityProvidersOptions = (options?: Options<GetLoginIdentityProvidersData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getLoginIdentityProviders({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true
      })
      return data
    },
    queryKey: getLoginIdentityProvidersQueryKey(options)
  })
}

export const loginIdentityProviderQueryKey = (options: Options<LoginIdentityProviderData>) => createQueryKey('loginIdentityProvider', options)

/**
 * Login via the identity provider
 * Logs in a Site Administrator to the Site Manager using the identity provider and sets the Session.
 *
 */
export const loginIdentityProviderOptions = (options: Options<LoginIdentityProviderData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await loginIdentityProvider({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true
      })
      return data
    },
    queryKey: loginIdentityProviderQueryKey(options)
  })
}

export const logoutQueryKey = (options?: Options<LogoutData>) => createQueryKey('logout', options)

/**
 * Logout
 * Logs out the user and clears the session.
 *
 */
export const logoutOptions = (options?: Options<LogoutData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await logout({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true
      })
      return data
    },
    queryKey: logoutQueryKey(options)
  })
}

/**
 * Logout
 * Logs out the user and clears the session.
 *
 */
export const logoutMutation = (options?: Partial<Options<LogoutData>>): UseMutationOptions<unknown, AxiosError<LogoutError>, Options<LogoutData>> => {
  const mutationOptions: UseMutationOptions<unknown, AxiosError<LogoutError>, Options<LogoutData>> = {
    mutationFn: async (localOptions) => {
      const { data } = await logout({
        ...options,
        ...localOptions,
        throwOnError: true
      })
      return data
    }
  }
  return mutationOptions
}

export const getSiteQueryKey = (options?: Options<GetSiteData>) => createQueryKey('getSite', options)

/**
 * Get the details of the Site
 */
export const getSiteOptions = (options?: Options<GetSiteData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getSite({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true
      })
      return data
    },
    queryKey: getSiteQueryKey(options)
  })
}

export const listMapsQueryKey = (options?: Options<ListMapsData>) => createQueryKey('listMaps', options)

/**
 * List Maps
 * Returns the list of Maps that the user has access to. This means that unauthenticated users will only see public Maps, while authenticated users may see additional Maps based on their permissions.
 *
 */
export const listMapsOptions = (options?: Options<ListMapsData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await listMaps({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true
      })
      return data
    },
    queryKey: listMapsQueryKey(options)
  })
}

export const getMapQueryKey = (options: Options<GetMapData>) => createQueryKey('getMap', options)

/**
 * Get the information of the Map
 */
export const getMapOptions = (options: Options<GetMapData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await getMap({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true
      })
      return data
    },
    queryKey: getMapQueryKey(options)
  })
}

export const listDatasetsQueryKey = (options: Options<ListDatasetsData>) => createQueryKey('listDatasets', options)

/**
 * List the Datasets available in the Map
 */
export const listDatasetsOptions = (options: Options<ListDatasetsData>) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await listDatasets({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true
      })
      return data
    },
    queryKey: listDatasetsQueryKey(options)
  })
}
